/**
 * Product releases ("News & Changelog") — kinds, categories and version maths.
 *
 * The version string is MAJOR.MINOR.PATCH, and it is ALSO stored as three
 * integers. That is not redundancy for its own sake: as a string, '1.10.0'
 * sorts BEFORE '1.9.0' ('1' < '9' at the third character), and a changelog in
 * the wrong order looks broken to the one audience this feature exists to
 * impress. Every ordering in the system goes through the integers; the string
 * is for display and for URLs.
 *
 * WHAT THE THREE PARTS MEAN
 *   MAJOR — a breaking change, or a landmark capability.
 *   MINOR — new features, backward compatible. (The product owner calls this an
 *           "intermediary" release.)
 *   PATCH — fixes and small improvements.
 */
export const RELEASE_KINDS = ['MAJOR', 'MINOR', 'PATCH'];
/**
 * Rendered as "New features" / "Quality of life" / "Bug fixes", in that order.
 * The order of this array IS the display order.
 */
export const RELEASE_CATEGORIES = ['FEATURE', 'IMPROVEMENT', 'FIX'];
/** Page size for the release list. Releases carry their entries, so keep it modest. */
export const RELEASE_PAGE_SIZE = 10;
export const RELEASE_MAX_PAGE_SIZE = 50;
/**
 * Digits only, no leading zeros, no `v` prefix, no pre-release suffix.
 * Strict on purpose: '1.09.0' and '1.9.0' must not both be storable, because
 * they would be the same release under two spellings.
 */
const VERSION_PATTERN = /^(0|[1-9]\d{0,5})\.(0|[1-9]\d{0,5})\.(0|[1-9]\d{0,5})$/;
/** Parse 'MAJOR.MINOR.PATCH', or return null when it is not exactly that. */
export function parseVersion(version) {
    const m = VERSION_PATTERN.exec(version);
    if (!m)
        return null;
    return { major: Number(m[1]), minor: Number(m[2]), patch: Number(m[3]) };
}
export const formatVersion = (v) => `${v.major}.${v.minor}.${v.patch}`;
/** Numeric comparison: negative when a is older than b, positive when newer. */
export function compareVersions(a, b) {
    return a.major - b.major || a.minor - b.minor || a.patch - b.patch;
}
/**
 * Which part of the version moved between two releases — and therefore what
 * kind of release the newer one is.
 *
 * The seed asserts that each release's declared `kind` equals this, so the
 * label and the number can never disagree. `from` is null for the first release
 * ever, which is measured against 0.0.0.
 */
export function kindOfBump(from, to) {
    const base = from ?? { major: 0, minor: 0, patch: 0 };
    if (to.major !== base.major)
        return 'MAJOR';
    if (to.minor !== base.minor)
        return 'MINOR';
    return 'PATCH';
}
//# sourceMappingURL=releases.js.map